import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BUSINESS } from "@/lib/site";

const LINKS = [
  { name: "Services", href: "/#services" },
  { name: "How We Work", href: "/#process" },
  { name: "Why Us", href: "/#manifesto" },
  { name: "Contact", href: "/#contact" },
];

const Logo = () => (
  <Link
    to="/"
    data-testid="nav-logo"
    className="flex items-center gap-2.5 group"
    onClick={() => window.scrollTo({ top: 0 })}
  >
    <span className="relative flex h-8 w-8 items-center justify-center">
      <span className="absolute inset-0 rounded-md border border-amber-500/40 rotate-45 transition-transform duration-500 group-hover:rotate-[135deg]" />
      <span className="h-2 w-2 rounded-full bg-amber-400" />
    </span>
    <span className="font-display text-[15px] font-bold tracking-tight text-white leading-none">
      Rolling Stone
      <span className="block text-[10px] font-mono font-normal uppercase tracking-[0.28em] text-amber-400/80 mt-0.5">
        Marketing
      </span>
    </span>
  </Link>
);

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0a0d14]/80 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Logo />

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <a
                key={l.name}
                href={l.href}
                data-testid={`nav-link-${l.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm text-slate-300 hover:text-amber-400 transition-colors duration-200"
              >
                {l.name}
              </a>
            ))}
            <a
              href="/#contact"
              data-testid="nav-cta"
              className="rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-[#0a0d14] hover:bg-amber-400 transition-colors duration-200"
            >
              Get a Data Strategy
            </a>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="md:hidden backdrop-blur-xl bg-[#0a0d14]/95 border-b border-white/10 px-4 py-5 space-y-1"
        >
          {LINKS.map((l) => (
            <a
              key={l.name}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-3 text-slate-200 hover:text-amber-400 transition-colors"
            >
              {l.name}
            </a>
          ))}
          <a
            href="/#contact"
            onClick={() => setOpen(false)}
            className="block mt-3 rounded-full bg-amber-500 px-5 py-3 text-center text-sm font-semibold text-[#0a0d14]"
          >
            Get a Data Strategy
          </a>
        </div>
      )}
    </header>
  );
}
